import { MOTION, RADIUS, SHADOW } from "@elyxeer/ui";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import type { EventLinkState } from "~/components/events/event-origin";
import { EventStateBadge } from "~/components/events/EventStateBadge";
import { EventTime } from "~/components/events/EventTime";
import { ArticleCover } from "~/components/news/ArticleCover";
import type { EventSummary } from "~/types/event";

/** The nearest event inside the dashboard's six-calendar-month horizon. */
export function UpcomingEvent({ event }: { event: EventSummary }) {
  const { t } = useTranslation();

  return (
    <Card
      variant="outlined"
      data-testid="events.upcoming"
      sx={{
        borderColor: "primary.main",
        borderRadius: `${RADIUS.lg}px`,
        transition: `box-shadow ${MOTION.transition}, transform ${MOTION.transition}`,
        "&:hover": { boxShadow: SHADOW.raised, transform: "translateY(-2px)" },
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={`/events/${event.slug}`}
        state={{ from: "dashboard" } satisfies EventLinkState}
        sx={{
          display: "flex",
          alignItems: "stretch",
          justifyContent: "flex-start",
        }}
      >
        <Box
          sx={{
            flex: 1,
            padding: { xs: 2.5, md: 4 },
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <EventTime event={event} withDate />
            <EventStateBadge state={event.state} />
          </Box>

          <Typography variant="H3" sx={{ color: "text.primary" }}>
            {event.title}
          </Typography>

          {event.summary ? (
            <Typography
              variant="T2"
              sx={{
                color: "text.secondary",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {event.summary}
            </Typography>
          ) : null}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              color: "link.main",
              marginTop: 0.5,
            }}
          >
            <Typography variant="L2" sx={{ color: "inherit" }}>
              {t("events.upcomingAction")}
            </Typography>
            <ArrowForwardIcon sx={{ fontSize: "1rem" }} />
          </Box>
        </Box>

        {/* The news and highlight cards already lead with artwork on phones. */}
        {event.cover_image_url ? (
          <ArticleCover
            slug={event.slug}
            src={event.cover_image_url}
            alt=""
            sx={{
              display: { xs: "none", md: "block" },
              alignSelf: "center",
              borderRadius: 0,
              flexShrink: 0,
              width: "34%",
            }}
          />
        ) : null}
      </CardActionArea>
    </Card>
  );
}
